// src/lib/lead/extraction/applyExtraction.ts
import type { ExtractedLeadV2, Lead, LockedFields } from "../../../types/lead";
import { extractCandidatePhonesFromTranscript, formatPhoneStrict } from "@/lib/lead/phones";
import {
  DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY,
  dueHoursForInterestLevel,
  nextActionForInterestLevel,
  type LeadDefaultFollowUpPolicy,
} from "@/lib/lead/extraction/leadDefaultFollowUpPolicy";
import {
  extractLeadSourceFromText,
  isLikelyInterestPhraseName,
} from "./textHeuristicsHelpers";
import {
  mergeLeadFromTranscript,
  type MergeLeadResult,
} from "@/lib/lead/extraction/mergeLeadFromTranscript";

export type TextHeuristics = {
  normalizePhone: (phone: string | null | undefined) => string;
  isValidEmail: (email: string | null | undefined) => boolean;

  extractPhoneCandidateFromText: (text: string) => string | null;
  extractEmailFromText: (text: string) => string | null;
  extractFullNameFromText: (text: string) => { first: string; last: string } | null;
  extractSizeOptionsFromText: (text: string) => string[];
  extractProductSubtypeFromText: (text: string) => string | null;

  shouldClearBudgetAsPhoneFragment: (budget: string | null, phone: string | null, text: string) => boolean;
  extractBudgetCandidatesFromText: (text: string) => {
    cash?: string | null;
    monthly?: string | null;
    paymentAmbiguous?: boolean | string | null;
  };
  consolidateBudget: (cash?: string | null, monthly?: string | null, paymentAmbiguous?: boolean | string | null) => string | null;

  sanitizeCustomerPhone: (phone: string | null, transcript: string) => string | null;

  stripLockedFields: (
    incoming: ExtractedLeadV2, 
    locks?: LockedFields
  ) => Partial<ExtractedLeadV2>;
  mergePreferExistingNonBlank: <T extends Record<string, any>>(base: T, incoming: T) => T;
};

export type ApplyExtractionArgs = {
  lead: Lead;
  leadId: string;
  transcript: string;
  extracted: ExtractedLeadV2;
  h: TextHeuristics;
  /** When absent, defaults to no automatic first follow-up (org must opt in via checklist config). */
  followUpPolicy?: LeadDefaultFollowUpPolicy;
  /**
   * Phase 2 of transcript merge v2 — invoked when the merge produces
   * conflicts. The returned lead still has the typed values preserved, so
   * the caller can safely show the modal asynchronously without blocking
   * the commit.
   */
  onMergeConflicts?: (args: { leadId: string; mergeResult: MergeLeadResult; source: "extract" }) => void;
};

export function applyExtractionToLead({
  lead,
  leadId,
  transcript,
  extracted,
  h,
  followUpPolicy = DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY,
  onMergeConflicts,
}: ApplyExtractionArgs): Lead | null {
  if (!lead || lead.id !== leadId) return null;

  const text = (transcript || "").trim();
  if (!text) return null;

  const patched: ExtractedLeadV2 = { ...extracted };

  // Prefer explicit spoken phone cues from transcript over model guesses.
  const phoneFromText = h.extractPhoneCandidateFromText(text);
  const existingPhoneDigits = h.normalizePhone(patched.phone);
  if (phoneFromText) {
    patched.phone = phoneFromText;
  } else if (!patched.phone || (existingPhoneDigits.length !== 7 && existingPhoneDigits.length !== 10)) {
    // Fallback when no explicit cue was detected.
    const fallbackPhoneFromText = h.extractPhoneCandidateFromText(text);
    if (fallbackPhoneFromText) patched.phone = fallbackPhoneFromText;
  }

  // Ensure we don't keep a lot/office/BDC number as the customer phone
  patched.phone = h.sanitizeCustomerPhone(patched.phone, text);
  // Defensive fallback: if phone is still empty, use the last phone-like number spoken in transcript.
  if (!patched.phone) {
    const candidates = extractCandidatePhonesFromTranscript(text);
    const lastCandidate = candidates.length > 0 ? candidates[candidates.length - 1] : null;
    const strict = formatPhoneStrict(lastCandidate);
    if (strict) patched.phone = strict;
  }

  // Backfill email from transcript if missing/invalid
  if (!h.isValidEmail(patched.email)) {
    const emailFromText = h.extractEmailFromText(text);
    patched.email = emailFromText && h.isValidEmail(emailFromText) ? emailFromText : null;
  }

  // Backfill name from transcript if missing (or if model misparsed "I'm interested in").
  if (isLikelyInterestPhraseName(patched.first_name, patched.last_name)) {
    patched.first_name = null;
    patched.last_name = null;
  }
  if (!patched.first_name || !patched.last_name) {
    const nameFromText = h.extractFullNameFromText(text);
    if (nameFromText) {
      if (!patched.first_name) patched.first_name = nameFromText.first;
      if (!patched.last_name) patched.last_name = nameFromText.last;
    }
  }

  if (!patched.lead_source_category) {
    const sourceFromText = extractLeadSourceFromText(text);
    if (sourceFromText) {
      patched.lead_source_category = sourceFromText.category;
      if (!patched.lead_source_detail && sourceFromText.detail) {
        patched.lead_source_detail = sourceFromText.detail;
      }
    }
  }

  // Backfill sizes from transcript if missing
  const sizes = h.extractSizeOptionsFromText(text);
  if (sizes.length > 0) {
    if (!patched.size_options || patched.size_options.length === 0) patched.size_options = sizes;
    if (!patched.size_config) patched.size_config = sizes[0];
  }

  // Backfill subtype if missing
  if (!patched.product_subtype) {
    const subtype = h.extractProductSubtypeFromText(text);
    if (subtype) patched.product_subtype = subtype;
  }

  // Budget guardrails: clear obvious phone-fragment misfires
  if (h.shouldClearBudgetAsPhoneFragment(patched.enunciated_budget, patched.phone, text)) {
    patched.enunciated_budget = null;
  }

  // Budget enrichment: keep BOTH cash + monthly if spoken
  const budgetFromText = h.extractBudgetCandidatesFromText(text);
  const paymentAmbiguous =
  typeof budgetFromText.paymentAmbiguous === "string"
    ? budgetFromText.paymentAmbiguous.trim().length > 0
    : !!budgetFromText.paymentAmbiguous;

  const consolidated = h.consolidateBudget(
    budgetFromText.cash ?? null,
    budgetFromText.monthly ?? null,
    paymentAmbiguous
  );


  if (consolidated) {
    if (!patched.enunciated_budget) {
      patched.enunciated_budget = consolidated;
    } else {
      const existing = patched.enunciated_budget.toLowerCase();
      const needCash =
        budgetFromText.cash &&
        !existing.includes(budgetFromText.cash.toLowerCase().replace(/\s+/g, " "));
      const needMonthly =
        budgetFromText.monthly &&
        !existing.includes("/month") &&
        !existing.includes("per month") &&
        !existing.includes("a month");

      if (needCash || needMonthly) {
        const cashPart = existing.includes("$") ? patched.enunciated_budget : budgetFromText.cash;
        patched.enunciated_budget =
          h.consolidateBudget(cashPart || (budgetFromText.cash ?? null), (budgetFromText.monthly ?? null)) ||
          patched.enunciated_budget;
      }
    }
  }

  // Phase 1 of transcript merge v2: cleared-wins via `user_overrides`.
  // Manual overrides surface as conflicts; this call site (Phase 1) keeps
  // the typed value on conflict, matching today's behavior. The Phase 2
  // conflict modal will resolve them interactively.
  const locks = (lead.lockedFields || {}) as LockedFields;
  const extractedFullName = [patched.first_name, patched.last_name].filter(Boolean).join(" ").trim();
  const baseExtracted = (lead.extracted || {}) as ExtractedLeadV2;

  const mergeOutcome = mergeLeadFromTranscript({
    baseExtracted,
    userOverrides: lead.user_overrides,
    lockedFields: locks,
    fromTranscript: patched,
  });
  const mergedExtracted = mergeOutcome.nextExtracted;
  const nextUserOverrides = mergeOutcome.nextUserOverrides;
  if (mergeOutcome.conflicts.length > 0) {
    if (typeof console !== "undefined" && console.debug) {
      console.debug(
        "[mergeLeadFromTranscript] conflicts:",
        mergeOutcome.conflicts.map((c) => c.field),
      );
    }
    // Phase 2: surface to the shell for the conflict modal. The Lead being
    // returned already preserves the typed values, so cancelling the modal
    // leaves the lead in a consistent state.
    onMergeConflicts?.({ leadId, mergeResult: mergeOutcome, source: "extract" });
  }
  // Phone-specific post-merge override removed in transcript-merge v2 Phase 2:
  // `mergeLeadFromTranscript` now gates transcript phone values through
  // `isValidTranscriptPhone` so only valid 10/11-digit values participate in
  // the merge. This makes phone behavior consistent with every other field
  // (manual override → conflict, cleared → transcript wins, no override →
  // existing meaningful wins) while still protecting against the historical
  // pattern of transcript extraction producing garbage phone strings.

  const existingFollowUps = Array.isArray(lead.follow_ups) ? lead.follow_ups : [];
  const hasOpenFollowUp = existingFollowUps.some((f) => f.status === "open");
  const followUps =
    followUpPolicy.enabled && !hasOpenFollowUp
      ? [
          ...existingFollowUps,
          {
            id: crypto.randomUUID(),
            next_action: nextActionForInterestLevel(mergedExtracted.interest_level, followUpPolicy),
            due_date: new Date(
              Date.now() + dueHoursForInterestLevel(mergedExtracted.interest_level, followUpPolicy) * 60 * 60 * 1000
            ).toISOString(),
            reason: "Organization default first follow-up (Location Tools → Sales daily checklist)",
            status: "open" as const,
            crm_sync_status: "pending" as const,
          },
        ]
      : existingFollowUps;

  return {
    ...lead,
    name: extractedFullName || lead.name,
    transcript: text,
    extracted: mergedExtracted,
    lastUpdated: Date.now(),
    lockedFields: locks,
    user_overrides: nextUserOverrides,
    needs_review: true,
    follow_ups: followUps,
  };
}